import type { AriaRole, ReactNode } from 'react';
import { BannerIcon } from './BannerIcon.js';
import { BannerProvider } from './BannerContext.js';
import { cva } from 'class-variance-authority';
import { clsx } from 'clsx';
import { MicroButton } from '../Button/MicroButton.js';

import s from './Banner.module.css';
import { Flex } from '../Flex/Flex.js';
import type { BannerTypes } from './Banner.types.js';

const bannerVariants = cva(s.banner, {
  variants: {
    variant: {
      default: s.default,
      info: s.info,
      warning: s.warning,
      error: s.error,
      success: s.success,
    },
  },
});

interface BannerProps {
  children: ReactNode;
  variant?: BannerTypes;
  ariaLabel: string;
  onClose?: () => void;
}

const Banner = ({
  variant = 'default',
  children,
  ariaLabel,
  onClose,
}: BannerProps) => {
  const handleBannerCloseClick = () => {
    if (onClose) onClose();
  };

  const isCritical = variant === 'error' || variant === 'warning';

  let role: AriaRole = 'status';
  if (isCritical) {
    role = onClose ? 'alertdialog' : 'alert';
  }

  return (
    <BannerProvider value={{ variant }}>
      <Flex
        gap="g-8"
        role={role}
        aria-label={ariaLabel}
        className={clsx(bannerVariants({ variant }))}
      >
        <BannerIcon />
        <Flex direction="column" gap="g-12">
          {children}
        </Flex>
        {onClose && (
          <MicroButton
            icon="close"
            size="sm"
            label={ariaLabel}
            onClick={handleBannerCloseClick}
            variant="tertiary"
          />
        )}
      </Flex>
    </BannerProvider>
  );
};

export { Banner };
